"use client";

import { FC, useMemo } from "react";
import { Control, FieldValues, UseFormRegister, UseFormWatch } from "react-hook-form";

import { FieldTypes } from "@types";
import type { Field } from "@types";

import { InputField } from "./InputField";
import { TextAreaField } from "./TextAreaField";
import { CheckboxField } from "./CheckboxField";
import { HiddenField } from "./HiddenField";
import s from "./Field.module.scss";
import { DropdownFilter } from "../filters";
import { SingleCheckboxField } from "./SingleCheckboxField";

interface Props {
  field: Field;
  register: UseFormRegister<FieldValues>;
  watch: UseFormWatch<FieldValues>;
  control: Control;
}

const FieldComponent: FC<Props> = ({ field, register, watch, control }) => {
  const conditionalArray: string[][] | null = useMemo(
    () => (field.conditional ? field.conditional.split(",").map((cf) => cf.split(":")) : null),
    [field.conditional],
  );
  const conditionalFieldValues = conditionalArray
    ? watch(conditionalArray.map(([key]) => key))
    : null;

  const conditionalHidden = useMemo(() => {
    if (conditionalFieldValues && conditionalArray && field.conditional !== "undefined") {
      return conditionalArray.some(([key, value], i) => {
        if (value === undefined) {
          return typeof conditionalFieldValues[i] === "undefined";
        }
        return conditionalFieldValues[i]?.toString() !== value;
      });
    }
    return false;
  }, [conditionalFieldValues, conditionalArray]);

  const content = useMemo(() => {
    switch (field.type) {
      case FieldTypes.text:
      case FieldTypes.email:
      case FieldTypes.tel:
        return <InputField {...field} register={register} key={field.id} />;
      case FieldTypes.textArea:
        return <TextAreaField {...field} register={register} key={field.id} />;
      case FieldTypes.checkbox:
        return <CheckboxField {...field} register={register} key={field.id} />;
      case FieldTypes.singleCheckbox:
        return <SingleCheckboxField {...field} register={register} key={field.id} />;
      case FieldTypes.select:
        return <DropdownFilter {...field} control={control} key={field.id} />;
      case FieldTypes.hidden:
        return <HiddenField {...field} register={register} key={field.id} />;
      default:
        return null;
    }
  }, [field, register, control]);

  if (conditionalHidden) return null;

  return <div className={s.Field}>{content}</div>;
};

export default FieldComponent;
